import { useState, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { logger } from '@/utils/productionLogger';
import type { ReserveCreditResponse } from '@/types/credits';

/**
 * Hook useCreditReservation - Gestion des réservations de crédit
 * 
 * Ce hook gère:
 * - La création de réservations de crédit avant le lancement de campagne
 * - La vérification et le renouvellement des réservations expirées
 * - La gestion des erreurs de réservation
 * 
 * Requirements: 1.1, 1.5, 3.1, 3.2
 */
export const useCreditReservation = () => {
  const [reserving, setReserving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  /**
   * Réserve un crédit pour une campagne
   * Requirement 1.1: Vérifier le solde et créer une réservation avant de lancer
   * Requirement 3.1: Afficher la modale d'achat si crédits insuffisants
   */
  const reserveCredit = useCallback(async (
    organizationId: string,
    campaignId: string
  ): Promise<ReserveCreditResponse> => {
    try {
      setReserving(true);
      setError(null);

      logger.debug('Reserving credit', { organizationId, campaignId });

      const { data, error: rpcError } = await supabase.rpc('reserve_credit', {
        p_organization_id: organizationId,
        p_campaign_id: campaignId
      });

      if (rpcError) {
        logger.error('RPC error reserving credit:', rpcError);
        throw rpcError;
      }

      const response = data as unknown as ReserveCreditResponse;

      if (!response.success) {
        logger.warn('Credit reservation failed', response);
        setError(response.error || 'Unknown error');
      } else {
        logger.debug('Credit reserved successfully', response);
      }

      return response;

    } catch (err) {
      const error = err instanceof Error ? err : new Error('Unknown error');
      logger.error('Error reserving credit:', error);
      setError(error.message);
      
      return {
        success: false,
        error: error.message
      };
    } finally {
      setReserving(false);
    }
  }, []);

  /**
   * Vérifie si une réservation existe et est valide, sinon en crée une nouvelle
   * Requirement 1.5: Vérifier la réservation au Step 4
   * Requirement 3.2: Afficher la modale d'achat si réservation expirée et pas de crédits
   */
  const checkAndRenewReservation = useCallback(async (
    organizationId: string,
    campaignId: string
  ): Promise<{
    success: boolean;
    hasValidReservation: boolean;
    needsCredits: boolean;
    error?: string;
  }> => {
    try {
      setError(null);

      logger.debug('Checking reservation', { organizationId, campaignId });

      // Vérifier si une réservation active existe
      const { data: existingReservation, error: queryError } = await supabase
        .from('credit_reservations')
        .select('*')
        .eq('campaign_id', campaignId)
        .eq('consumed', false)
        .gt('expires_at', new Date().toISOString())
        .maybeSingle();

      if (queryError) {
        logger.error('Error checking reservation:', queryError);
        throw queryError;
      }

      // Si réservation valide existe, OK
      if (existingReservation) {
        logger.debug('Valid reservation found', existingReservation);
        return {
          success: true,
          hasValidReservation: true,
          needsCredits: false
        };
      }

      // Pas de réservation valide, tenter d'en créer une nouvelle
      logger.debug('No valid reservation, attempting to create new one');
      const reservationResult = await reserveCredit(organizationId, campaignId);

      if (reservationResult.success) {
        return {
          success: true,
          hasValidReservation: true,
          needsCredits: false
        };
      }

      // Échec de réservation - vérifier si c'est un problème de crédits
      const needsCredits = reservationResult.error === 'insufficient_credits';
      
      return {
        success: false,
        hasValidReservation: false,
        needsCredits,
        error: reservationResult.error
      };

    } catch (err) {
      const error = err instanceof Error ? err : new Error('Unknown error');
      logger.error('Error checking and renewing reservation:', error);
      setError(error.message);
      
      return {
        success: false,
        hasValidReservation: false,
        needsCredits: false,
        error: error.message
      };
    }
  }, [reserveCredit]);

  /**
   * Libère manuellement une réservation (si nécessaire)
   * Note: Les réservations expirent automatiquement après 30 minutes
   */
  const releaseReservation = useCallback(async (
    campaignId: string
  ): Promise<boolean> => {
    try {
      logger.debug('Releasing reservation', { campaignId });

      const { error: deleteError } = await supabase
        .from('credit_reservations')
        .delete()
        .eq('campaign_id', campaignId)
        .eq('consumed', false);

      if (deleteError) {
        logger.error('Error releasing reservation:', deleteError);
        throw deleteError;
      }

      logger.debug('Reservation released successfully');
      return true;

    } catch (err) {
      const error = err instanceof Error ? err : new Error('Unknown error');
      logger.error('Error releasing reservation:', error);
      return false;
    }
  }, []);

  return {
    reserveCredit,
    checkAndRenewReservation,
    releaseReservation,
    reserving,
    error
  };
};
